/**
 * Printable rows for cutsheet specifications rendered as markable choices —
 * every option of a select/multiselect spec printed with a checkbox, for the
 * customer to tick on paper (or, on a filled sheet with `showAllSpecOptions`,
 * with the chosen one already ticked).
 *
 * Shared by the API's PDFs and the web print-designer preview, so the preview
 * words and marks options exactly as the printout does.
 */

import { specOptionValue, type SpecOptionLike } from './cutsheet-spec-options';

/** One option of a spec, as printed. */
export interface SpecChoice {
  label: string;
  isSelected: boolean;
}

/** A spec as printed on the sheet: options to mark, or a write-in value. */
export interface SpecChoiceRow {
  name: string;
  unit?: string;
  /** Present for select/multiselect specs that have options. */
  choices?: SpecChoice[];
  /** Write-in value for text/number specs ('' on a blank form). */
  value?: string;
}

/** The parts of a specification definition a printed row needs. */
export interface SpecChoiceDefinition {
  title: string;
  printedText?: string | null;
  inputType: 'text' | 'number' | 'select' | 'multiselect';
  options?: unknown;
  unit?: string | null;
}

const text = (raw: unknown): string =>
  typeof raw === 'string' ? raw.trim() : '';

/**
 * A stored answer as a list of non-empty strings. An unanswered select saved
 * as '' is no answer at all — it must not match anything.
 * @param answer - A value, an array of values, or nothing
 */
function answeredValues(answer: unknown): string[] {
  let raw: unknown[] = [];
  if (Array.isArray(answer)) {
    raw = answer;
  } else if (answer != null) {
    raw = [answer];
  }
  return raw.map((v) => String(v).trim()).filter(Boolean);
}

/**
 * The printed text of an option: its Printed Text override, else its label.
 * @param option - A rich option object or a legacy plain-string option
 */
export function specChoiceLabel(option: string | SpecOptionLike): string {
  if (typeof option === 'string') return text(option);
  return (
    text(option?.printedText) ||
    text(option?.label) ||
    text(option?.title) ||
    specOptionValue(option ?? {})
  );
}

/**
 * Lists a spec's options for printing, marking the answered ones. Options keep
 * catalog order. An answer is matched on the option's `value` (what responses
 * store), falling back to its label for legacy plain-string answers; when two
 * options collide on `value`, only the first is marked — the same first-wins
 * rule as `buildOptionLabelMap`, so the mark lands where the label resolves.
 *
 * An answer that matches no option (the option was renamed or removed after
 * the customer answered) is appended, marked, so the customer's actual answer
 * still reaches the cutting room.
 * @param options - A spec definition's `options[]` (rich objects or strings)
 * @param answer - The stored answer: a value, an array of values, or nothing
 */
export function specOptionChoices(
  options: unknown,
  answer?: unknown,
): SpecChoice[] {
  if (!Array.isArray(options)) return [];
  const answered = answeredValues(answer);
  const matched = new Set<string>();
  const markedValues = new Set<string>();
  const choices: SpecChoice[] = [];
  const list = options as (string | SpecOptionLike)[];
  // The label fallback is only for an answer that is no option's value, so a
  // value match always wins over another option's same-named label.
  const optionValues = new Set(list.map((opt) => specOptionValue(opt ?? {})));
  for (const opt of list) {
    const label = specChoiceLabel(opt);
    if (!label) continue;
    const value = specOptionValue(opt ?? {});
    const legacyLabel = typeof opt === 'string' ? '' : text(opt?.label);
    const hits = answered.filter(
      (a) =>
        (value && a === value) ||
        (legacyLabel && a === legacyLabel && !optionValues.has(a)),
    );
    hits.forEach((a) => matched.add(a));
    const isSelected = hits.length > 0 && !markedValues.has(value);
    if (isSelected) markedValues.add(value);
    choices.push({ label, isSelected });
  }
  if (choices.length === 0) return choices;
  for (const a of answered) {
    if (!matched.has(a)) {
      matched.add(a);
      choices.push({ label: a, isSelected: true });
    }
  }
  return choices;
}

/**
 * Builds the printed row for one spec. Select/multiselect specs with options
 * become choices; anything else is a write-in carrying the answer (if any).
 * @param def - The specification definition
 * @param answer - The stored answer, omitted on a blank form
 */
export function buildSpecChoiceRow(
  def: SpecChoiceDefinition,
  answer?: unknown,
): SpecChoiceRow {
  const name = def.printedText || def.title;
  const unit = def.unit || undefined;
  if (def.inputType === 'select' || def.inputType === 'multiselect') {
    const choices = specOptionChoices(def.options, answer);
    if (choices.length > 0) return { name, unit, choices };
  }
  let value = '';
  if (Array.isArray(answer)) {
    value = answer.join(', ');
  } else if (answer != null) {
    value = String(answer);
  }
  return { name, unit, value };
}
